import React from "react";

export default function AISoftwareProgressContributors() {
  // Set dimensions and margins
  const width = 1000;
  const height = 700;
  const margin = { top: 80, right: 40, bottom: 50, left: 180 }; // Increased left margin for threshold labels
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Month labels from data
  const months = [
    "Dec 2023", "Apr 2024", "Aug 2024", "Dec 2024", "Apr 2025", "Aug 2025", "Dec 2025",
    "Apr 2026", "Aug 2026", "Dec 2026", "Jan 2027", "Feb 2027", "Mar 2027", "Apr 2027",
    "May 2027", "Jun 2027", "Jul 2027", "Aug 2027", "Sep 2027", "Oct 2027"
  ];
  
  // The inflection point where AI-driven algorithms reach 50% of algorithm improvements
  const inflectionIndex = 9; // Dec 2026
  
  // Data arrays from the provided table
  const progressMultipliers = [
    1.00, 1.01, 1.03, 1.05, 1.13, 1.21, 1.30, 1.50, 1.73, 2.00, 
    2.50, 3.00, 4.00, 5.00, 7.00, 10.00, 15.00, 25.00, 50.00, 100.00
  ];
  
  // // Compute contribution percentages
  // const computePercentages = [
  //   50.00, 49.75, 49.27, 48.78, 47.00, 45.24, 43.48, 39.99, 36.60, 33.33,
  //   28.57, 25.00, 20.00, 16.67, 12.50, 9.09, 6.25, 3.85, 1.96, 0.99
  // ];

  // // Software contribution percentages (combined human and AI)
  // const softwarePercentages = computePercentages.map(p => 100 - p);
  
  // AI percentage of the software improvements
  const aiShareOfSoftware = [0,0.009800999999999997,0.08352100000000001,0.22657599999999994,1.2791610000000002,
    3.0275999999999996,5.326864,11.135568999999998,17.875984,25,34.809999999999995,44.44888900000001,60.84,73.462041,
    86.49000000000001,94.08999999999999,98.00999999999999,100,100,100];
  
  // Human percentage of the software improvements
  const humanShareOfSoftware = aiShareOfSoftware.map(p => 100 - p);
  
  // Calculate actual contribution percentages for each category
  // const computeContribution = computePercentages;
  const humanSoftwareContribution = humanShareOfSoftware
  const aiSoftwareContribution = aiShareOfSoftware

  // Threshold values and labels for the y-axis
  const thresholds = [
    { label: "Unreliable Agent", value: 1.05 },
    { label: "Reliable Agent", value: 1.3 },
    { label: "Superhuman\ncoder", value: 4 },
    { label: "Superhuman\nAI Researcher", value: 25 },
    { label: "Superhuman\nRemote Worker", value: 100 },
    { label: "Superintelligent\nAI Researcher", value: 250 },
    { label: "Generally\nSuperintelligent", value: 1000 },
    { label: "Wildly\nSuperintelligent", value: 15000 },
  ];

  // Limit our chart to show only up to "Generally superhuman" (100x)
  const displayThresholds = thresholds.filter(t => t.value <= 100);

  // Scales: x based on index, y based on value (logarithmic)
  const xScale = (i: number) => margin.left + (i / (months.length - 1)) * innerWidth;
  
  // Logarithmic scale for the y-axis
  const yScale = (value: number) => {
    const minLog = Math.log10(0.9); // Slightly below 1 to allow some margin
    const maxLog = Math.log10(100);  // Max value to display
    const logValue = Math.log10(Math.max(value, 0.9));
    return margin.top + innerHeight - ((logValue - minLog) / (maxLog - minLog)) * innerHeight;
  };

  // Generate x-values for all data points
  const xValues = months.map((_, i) => xScale(i));

  // Choose select x-axis tick indices
  const xTickIndices = [0, 3, 6, 9, 13, 19];
  
  // Generate path points for each category at each time point
  const generateDataPoints = () => {
    const points = [];
    
    for (let i = 0; i < months.length; i++) {
      const x = xScale(i);
      const multiplier = progressMultipliers[i];
      
      // Calculate the actual height values based on percentage contributions
      // scaled by the total progress multiplier
      // const computeHeight = yScale(1 + (multiplier - 1) * (computeContribution[i] / 100));
      const humanSoftwareTop = yScale(1 + (multiplier - 1) * ((humanSoftwareContribution[i]) / 100));
      const aiSoftwareTop = yScale(multiplier);
      
      points.push({
        x,
        // computeBottom: yScale(1),
        // computeTop: computeHeight,
        humanSoftwareBottom: yScale(1),//computeHeight,
        humanSoftwareTop: humanSoftwareTop,
        aiSoftwareBottom: humanSoftwareTop,
        aiSoftwareTop: aiSoftwareTop,
      });
    }
    
    return points;
  };
  
  const dataPoints = generateDataPoints();
  
  // Generate area paths
  const generateAreaPath = (points: any[], getBottom: (p: any) => number, getTop: (p: any) => number): string => {
    // Draw the top curve from left to right
    let path = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${getTop(p)}`
    ).join(' ');
    
    // Then draw the bottom curve from right to left
    path += ' ' + points.slice().reverse().map((p, i) => 
      `L ${p.x} ${getBottom(p)}`
    ).join(' ');
    
    // Close the path
    path += ' Z';
    
    return path;
  };
  
  const computeArea = generateAreaPath(
    dataPoints,
    p => p.computeBottom,
    p => p.computeTop
  );
  
  const humanSoftwareArea = generateAreaPath(
    dataPoints,
    p => p.humanSoftwareBottom,
    p => p.humanSoftwareTop
  );
  
  const aiSoftwareArea = generateAreaPath(
    dataPoints,
    p => p.aiSoftwareBottom,
    p => p.aiSoftwareTop
  );

  // Updated colors using the scheme from the provided image
  const colors = {
    // compute: "#000100",      // Dark gray/black for compute
    humanAlgo: "#50a050",    // Medium gray for human-driven
    aiAlgo: "#004000",       // Green for AI-driven
    axis: "#444444",         // Dark gray for axis
    text: "#333333"          // Dark gray text
  };

  // Helper function to determine if a threshold is crossed in our data
  const isThresholdCrossed = (threshold: { value: number }) => {
    return progressMultipliers.some(multiplier => multiplier >= threshold.value);
  };

  // Find first point where threshold is crossed
  const getFirstCrossingX = (thresholdValue: number) => {
    for (let i = 0; i < progressMultipliers.length; i++) {
      if (progressMultipliers[i] >= thresholdValue) {
        return xScale(i);
      }
    }
    return null;
  };

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={52}
          y={45}
          fontSize="32"
          fontFamily="sans-monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          AI Software Progress Contributors: Human vs. AI
        </text>

        {/* Threshold lines with labels */}
        {displayThresholds.map((threshold, i) => {
          const y = yScale(threshold.value);
          if (!isThresholdCrossed(threshold)) return null;
          
          const firstCrossingX = getFirstCrossingX(threshold.value);
          if (firstCrossingX === null) return null;

          const lineCount = threshold.label.split('\n').length;
          const lineHeight = 11;
          const totalHeight = lineCount * lineHeight;
          const initialOffset = -(totalHeight / 2) + (lineHeight / 2);

          return (
            <g key={`threshold-${i}`}>
              <line 
                x1={margin.left} 
                y1={y} 
                x2={firstCrossingX} 
                y2={y} 
                stroke="#ddd" 
                strokeWidth="0.8" 
                strokeDasharray="5,5"
              />
              
              <text 
                x={margin.left - 10} 
                y={y + 2} 
                textAnchor="end" 
                className="text-[14px] font-mono md:text-[14px] sm:text-[14px]"
                fill={colors.text}
              >
                {threshold.label.split('\n').map((line, j) => (
                  <tspan 
                    key={j} 
                    x={margin.left - 10} 
                    dy={j === 0 ? initialOffset : lineHeight+5}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xValues[index];
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x} 
                y={height - margin.bottom / 2} 
                textAnchor="middle" 
                className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                fill={colors.text}
              >
                {months[index]}
              </text>
            </g>
          );
        })}

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
          fill={colors.text}
        >
          Capability Level (AI R&D Progress Multiplier)
        </text>

        {/* X-axis Label
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
          fill={colors.text}
        >
        Date
        </text> */}

        {/* Stacked areas (filled with colors) */}
        {/* <path 
          d={computeArea} 
          fill={colors.compute} 
          fillOpacity={0.9} 
          stroke="none"
        /> */}
        <path 
          d={humanSoftwareArea} 
          fill={colors.humanAlgo} 
          fillOpacity={0.9} 
          stroke="none"
        />
        <path 
          d={aiSoftwareArea} 
          fill={colors.aiAlgo} 
          fillOpacity={0.9} 
          stroke="none"
        />

        {/* Boundary lines for clarity */}
        {/* <path 
          d={dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.computeTop}`).join(' ')} 
          fill="none" 
          stroke={colors.compute} 
          strokeWidth="1" 
        /> */}
        <path 
          d={dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.humanSoftwareTop}`).join(' ')} 
          fill="none" 
          stroke={colors.humanAlgo} 
          strokeWidth="1" 
        />
        <path 
          d={dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.aiSoftwareTop}`).join(' ')} 
          fill="none" 
          stroke="#000" 
          strokeWidth="1" 
          opacity="0.5"
        />

        {/* Legend */}
        <g transform={`translate(${margin.left}, 80)`}>
          {/* Compute */}
          {/* <rect x="0" y="0" width="20" height="20" fill={colors.compute} />
          <text x="30" y="15" fill={colors.text} className="text-[16px] font-mono">Compute</text> */}
          
          {/* Human-driven software improvements */}
          {/* <rect x="0" y="30" width="20" height="20" fill={colors.humanAlgo} /> */}
          <text x="340" y="530" fill={"#111111"} className="text-[20px] font-mono font-bold">Human-driven progress</text>
          
          {/* AI-driven software improvements */}
          {/* <rect x="0" y="60" width="20" height="20" fill={colors.aiAlgo} /> */}
          <text x="540" y="400" fill={"#eeeeee"} className="text-[20px] font-mono font-bold">AI-driven progress</text>
        </g>

        {/* Annotations
        <text 
          x={xScale(16)} 
          y={yScale(progressMultipliers[16] * 0.6)} 
          textAnchor="middle" 
          className="text-[14px] font-mono font-bold"
          fill={colors.aiAlgo}
        >
          AI-driven improvements become dominant
        </text>
        
        {/* Exponential growth annotation */}
        {/* <text 
          x={xScale(18)} 
          y={yScale(progressMultipliers[18] * 0.5)} 
          textAnchor="middle" 
          className="text-[14px] font-mono font-bold"
          fill={colors.aiAlgo}
        >
          Exponential growth phase
        </text> */}
      </svg>
    </div>
  );
}